import {KinesisEventModel} from "./kinesis-event-model";
import {VendorConsentProvider} from "../../vendor-consent-provider";
import {ANALYTICS} from "../analytics-consts";
import {KinesisClient, PutRecordsCommand, PutRecordsRequestEntry} from "@aws-sdk/client-kinesis";
import {fromCognitoIdentityPool} from "@aws-sdk/credential-providers";
import {ARGS} from "../../../core/bootstrap/args";
import {runInAction} from "mobx"
import {CoralogixRum} from '@coralogix/browser';
import {CoralogixLogSeverity} from "@coralogix/browser/src/types-external";
import {KinesisRetryUtils} from "./kinesis-retry-utils";


export class KinesisProviderApi extends VendorConsentProvider {
  kinesis: KinesisClient;
  syncKinesis: KinesisClient;
  partitionKey: String;

  constructor({partitionKey, shouldSupportConsent}) {
    super(ANALYTICS.VENDORS_ID.awsKinesis, shouldSupportConsent);

    this.partitionKey = partitionKey;

    this.init()
  }

  init() {
    this.isProviderEnable ? this.enable(this.aws) : this.disable()
  }

  enable() {
    const region = ARGS.KINESIS_REGION_NAME;
    const identityPoolId = ARGS.KINESIS_IDENTITY_POOL_ID;
    const credentials = fromCognitoIdentityPool({
      identityPoolId,
      clientConfig: {
        region
      }
    });

    this.kinesis = new KinesisClient({
      region,
      credentials
    });

    this.syncKinesis = new KinesisClient({
      region,
      credentials,
      httpOptions: {
        xhrAsync: false
      }
    });
  }

  disable() {
    this.kinesis = null;
    this.syncKinesis = null;
  }

  async send(events, retryAttempt = 0) {
    const Records: Array = this.createRecords(events);
    const retryUtils = new KinesisRetryUtils();
    const maxRetries = retryUtils.getMaxRetries();

    try {
      const isSync = events.find((event: KinesisEventModel) => event.isSync);

      let response;

      if (isSync) {
        response = await this._sendSyncRecords(Records);
      } else {
        response = await this._sendRecords(Records);
      }

      // Handle partial batch failures - AWS returns a Records array matching input order
      const failedEvents = [];

      runInAction(() => {
        events.forEach((event, index) => {
          const record = response.Records[index];

          // Success: record has SequenceNumber
          if (record.SequenceNumber) {
            event.sent = true;
            event.lastRetryTimestamp = Date.now();
          }
          // Failure: record has ErrorCode
          else if (record.ErrorCode) {
            event.sent = false;
            event.lastErrorCode = record.ErrorCode;
            event.retryCount = (event.retryCount || 0) + 1;
            event.lastRetryTimestamp = Date.now();

            // Add to retry queue if error is retryable and under max retry limit
            if (retryUtils.isRetryableError(record.ErrorCode) && event.retryCount < maxRetries) {
              failedEvents.push(event);
            }
          }
        });
      });

      // Retry failed events with exponential backoff
      // failedEvents already filtered by event.retryCount < maxRetries, so just check if any exist
      if (failedEvents.length > 0) {
        const delay = retryUtils.getBackoffDelay(retryAttempt);
        await retryUtils.sleep(delay);
        return await this.send(failedEvents, retryAttempt + 1);
      }

    } catch (error) {
      // Network/SDK error - retry entire batch if retryable
      const errorIsRetryable = retryUtils.isRetryableError(error);

      // Increment retry count for all events and collect those still under budget
      const retryableEvents = [];
      runInAction(() => {
        events.forEach(event => {
          event.sent = false;
          event.lastErrorCode = error.name || error.code || 'UnknownError';
          event.retryCount = (event.retryCount || 0) + 1;
          event.lastRetryTimestamp = Date.now();

          // Check if this event still has retry budget
          if (errorIsRetryable && event.retryCount < maxRetries) {
            retryableEvents.push(event);
          }
        });
      });

      // Retry only events that haven't exceeded their retry budget
      if (retryableEvents.length > 0) {
        const delay = retryUtils.getBackoffDelay(retryAttempt);
        await retryUtils.sleep(delay);
        return await this.send(retryableEvents, retryAttempt + 1);
      }

      // Events already marked as failed and counted above
      console.error('Kinesis send error:', error);
    } finally {
      // Enhanced Coralogix logging with retry metadata
      events?.forEach(event => {
        CoralogixRum.log(
          event.sent ? CoralogixLogSeverity.Info : CoralogixLogSeverity.Error,
          `Event ${event.event_name} ${event.sent ? 'sent' : 'failed'}`,
          {
            isSync: event.isSync,
            source: event.properties.source,
            context: event.properties.context,
            eventName: event.event_name,
            screen: event.properties.screen,
            devicePlatformType: event.device_platform_type,
            timestamp: new Date().toISOString(),
            eventDatetime: event.event_datetime,
            retryCount: event.retryCount || 0,
            lastErrorCode: event.lastErrorCode || null,
            lastRetryTimestamp: event.lastRetryTimestamp || null,
            retryAttempt: retryAttempt
          });
      });
    }

    return events;
  }

  async updateUserData(eventData) {
    const Records: Array = this.createRecords([eventData]);

    await this._sendRecords(Records, "web_stream_users");

    return eventData;
  }

  async _sendRecords(Records: Array, StreamName = ARGS.KINESIS_STREAM_NAME || 'web_stream_events') {
    const params = {
      Records, StreamName, /* required */
    };

    const command = new PutRecordsCommand(params);

    return await this.kinesis?.send(command);
  }


  async _sendSyncRecords(Records: Array, StreamName = ARGS.KINESIS_STREAM_NAME || 'web_stream_events') {
    const params = {
      Records, StreamName, /* required */
    };

    const command = new PutRecordsCommand(params);

    return await this.syncKinesis.send(command);
  }

  createRecords(events): Array<PutRecordsRequestEntry> {
    var textEncoder = new TextEncoder(); // always utf-8

    // A random partition key per record spreads events evenly across Kinesis
    // shards. Using a stable per-visitor key (visitor_id) would funnel all of a
    // visitor's events to one shard and overload it (e.g. bots/crawlers).
    // visitor_id is still carried in the event payload for analytics.
    const records: Array<PutRecordsRequestEntry> = events.map(e => ({
      PartitionKey: this.createPartitionKey(), /* required */
      Data: textEncoder.encode(JSON.stringify(e))
    }));

    return records;
  }

  createPartitionKey(): String {
    if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
      return crypto.randomUUID();
    }

    // Fallback for non-secure contexts / older browsers.
    return `${this.partitionKey}-${Math.random().toString(36).slice(2)}`;
  }
}
